// frontend/src/pages/adolescentes/AdolescentesListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import PermissionGuard from '../../components/PermissionGuard';
import MovimentacoesGrid from '../../components/adolescentes/MovimentacoesGrid';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';
import { formatAdolescenteNome } from '../../utils/adolescenteNome';

const PANEL_THEME_STORAGE_KEY = 'panelLightMode';
const PANEL_THEME_EVENT = 'panel-theme-changed';
const SEARCH_DEBOUNCE_MS = 450;
const MIN_SEARCH_LENGTH = 2;

function AdolescentesListPage() {
  const { user } = useAuth();
  const canShowDeleteAdolescente =
    Array.isArray(user?.perfil?.permissions)
    && user.perfil.permissions.includes('usuarios.delete');

  const [adolescentes, setAdolescentes] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isListLoading, setIsListLoading] = useState(false);
  const [error, setError] = useState(null); // { kind: 'permission' } | AxiosError | null
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const normalizedSearch = debouncedSearch.trim();
  const effectiveSearch = normalizedSearch.length >= MIN_SEARCH_LENGTH ? normalizedSearch : '';
  const isSearchBelowMinLength =
    normalizedSearch.length > 0 && normalizedSearch.length < MIN_SEARCH_LENGTH;

  // Modal Visualizar Adolescente
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAdolescente, setSelectedAdolescente] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  // Modal Movimentações do Adolescente
  const [showMovsModal, setShowMovsModal] = useState(false);
  const [movsLoading, setMovsLoading] = useState(false);
  const [movsError, setMovsError] = useState(null);
  const [movsList, setMovsList] = useState([]);
  const [movsPagination, setMovsPagination] = useState(null);
  const [movsCurrentPage, setMovsCurrentPage] = useState(1);
  const [movsAdolescente, setMovsAdolescente] = useState(null); // guarda {id, nome}
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteCheckingFinalizada, setDeleteCheckingFinalizada] = useState(false);
  const [deleteHasFinalizada, setDeleteHasFinalizada] = useState(false);
  const [deletingAdolescente, setDeletingAdolescente] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [lightMode, setLightMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = sessionStorage.getItem(PANEL_THEME_STORAGE_KEY);
    if (saved === null) return true;
    return saved === 'true';
  });
  const panelTheme = lightMode ? 'light' : 'dark';
  const panelThemePageClassName = `adolescentes-page panel-theme panel-theme--${panelTheme}`;
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  // AbortControllers
  const listAbortRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const hasLoadedListRef = useRef(false);
  const viewAbortRef = useRef(null);
  const movsAbortRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncPanelTheme = (event) => {
      const nextMode = event?.detail?.lightMode;
      if (typeof nextMode === 'boolean') {
        setLightMode(nextMode);
        return;
      }

      const saved = sessionStorage.getItem(PANEL_THEME_STORAGE_KEY);
      setLightMode(saved !== 'false');
    };

    window.addEventListener(PANEL_THEME_EVENT, syncPanelTheme);
    return () => {
      window.removeEventListener(PANEL_THEME_EVENT, syncPanelTheme);
    };
  }, []);

  useEffect(() => {
    listAbortRef.current?.abort?.();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const requestId = ++listRequestIdRef.current;

    const fetchAdolescentes = async (page) => {
      setIsListLoading(hasLoadedListRef.current);
      setError(null);
      try {
        const effectivePerPage = perPage === 'all' ? 10000 : Number(perPage);
        const response = await api.get('/adolescentes', {
          params: {
            page,
            per_page: effectivePerPage,
            search: effectiveSearch || undefined,
          },
          signal: controller.signal,
        });

        if (controller.signal.aborted || requestId !== listRequestIdRef.current) {
          return;
        }

        setAdolescentes(response?.data?.data || []);
        setPaginationInfo(response?.data?.meta || null);
      } catch (err) {
        if (controller.signal.aborted || requestId !== listRequestIdRef.current) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted && requestId === listRequestIdRef.current) {
          hasLoadedListRef.current = true;
          setInitialLoading(false);
          setIsListLoading(false);
        }
      }
    };

    fetchAdolescentes(currentPage);
    return () => controller.abort();
  }, [currentPage, perPage, effectiveSearch, reloadKey]);

  const handlePageChange = (newPage) => {
    if (paginationInfo && newPage > 0 && newPage <= paginationInfo.last_page) {
      setCurrentPage(newPage);
    }
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : adolescentes.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const openViewModal = async (id) => {
    // aborta requisição anterior (se houver)
    viewAbortRef.current?.abort?.();
    const controller = new AbortController();
    viewAbortRef.current = controller;

    setShowViewModal(true);
    setViewLoading(true);
    setViewError(null);
    setSelectedAdolescente(null);
    try {
      const resp = await api.get(`/adolescentes/${id}`, { signal: controller.signal });
      setSelectedAdolescente(resp?.data?.data || null);
    } catch (err) {
      if (controller.signal.aborted) return;
      setViewError(
        err?.response?.status === 403
          ? 'Acesso não permitido.'
          : 'Não foi possível carregar os dados do adolescente.'
      );
    } finally {
      if (!controller.signal.aborted) setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    viewAbortRef.current?.abort?.();
    setShowViewModal(false);
    setSelectedAdolescente(null);
    setViewError(null);
  };

  // ------- Movimentações (modal) -------
  const fetchMovs = async (adolId, page = 1) => {
    movsAbortRef.current?.abort?.();
    const controller = new AbortController();
    movsAbortRef.current = controller;

    setMovsLoading(true);
    setMovsError(null);
    try {
      const resp = await api.get(`/adolescentes/${adolId}/movimentacoes?page=${page}`, {
        signal: controller.signal,
      });
      setMovsList(resp?.data?.data || []);
      setMovsPagination(resp?.data?.meta || null);
    } catch (err) {
      if (controller.signal.aborted) return;
      setMovsError(
        err?.response?.status === 403
          ? 'Acesso não permitido.'
          : 'Não foi possível carregar as movimentações.'
      );
      setMovsList([]);
      setMovsPagination(null);
    } finally {
      if (!controller.signal.aborted) setMovsLoading(false);
    }
  };

  const openMovsModal = (adol) => {
    const minimal = {
      id: adol.id,
      nome_adolescente: adol.nome_adolescente,
      nome_social: adol.nome_social,
    };
    setMovsAdolescente(minimal);
    setShowMovsModal(true);
    setMovsCurrentPage(1);
    fetchMovs(adol.id, 1);
  };

  const checkDeleteBlockedByFinalizada = async (adolescenteId) => {
    setDeleteCheckingFinalizada(true);
    try {
      const resp = await api.get('/movimentacoes', {
        params: {
          adolescente_id: adolescenteId,
          status: 'closed',
          per_page: 1,
        },
      });
      const totalFinalizadas = Number(resp?.data?.meta?.total || 0);
      setDeleteHasFinalizada(totalFinalizadas > 0);
    } catch (err) {
      setDeleteHasFinalizada(false);
      setDeleteError(
        err?.response?.status === 403
          ? 'Acesso não permitido para validar movimentações finalizadas.'
          : 'Não foi possível validar movimentações finalizadas.'
      );
    } finally {
      setDeleteCheckingFinalizada(false);
    }
  };

  const openDeleteModal = (adol) => {
    const minimal = {
      id: adol.id,
      nome_adolescente: adol.nome_adolescente,
      nome_social: adol.nome_social,
    };
    setDeleteTarget(minimal);
    setDeleteError(null);
    setDeleteHasFinalizada(false);
    setShowDeleteModal(true);

    setMovsAdolescente(minimal);
    setMovsCurrentPage(1);
    fetchMovs(adol.id, 1);
    checkDeleteBlockedByFinalizada(adol.id);
  };

  const handleMovsPageChange = (newPage) => {
    if (movsPagination && newPage > 0 && newPage <= movsPagination.last_page) {
      setMovsCurrentPage(newPage);
      fetchMovs(movsAdolescente.id, newPage);
    }
  };

  const clearMovsModalData = () => {
    movsAbortRef.current?.abort?.();
    setMovsList([]);
    setMovsPagination(null);
    setMovsError(null);
    setMovsAdolescente(null);
  };

  const closeMovsModal = () => {
    setShowMovsModal(false);
    clearMovsModalData();
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteError(null);
    setDeleteHasFinalizada(false);
    setDeleteCheckingFinalizada(false);
    setDeletingAdolescente(false);
    clearMovsModalData();
  };

  const handleDeleteAdolescente = async () => {
    if (!deleteTarget || deleteHasFinalizada || deleteCheckingFinalizada || deletingAdolescente) {
      return;
    }

    setDeletingAdolescente(true);
    setDeleteError(null);
    try {
      await api.delete(`/adolescentes/${deleteTarget.id}`);
      closeDeleteModal();

      if (adolescentes.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        setReloadKey((prev) => prev + 1);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Não foi possível excluir o adolescente.';
      setDeleteError(msg);
    } finally {
      setDeletingAdolescente(false);
    }
  };

  const fmtDate = (yyyy_mm_dd) => {
    if (!yyyy_mm_dd) return '—';
    const parts = String(yyyy_mm_dd).split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return yyyy_mm_dd;
  };

  const renderMovimentacoesList = ({ showNovaButton }) => (
    <MovimentacoesGrid
      loading={movsLoading}
      error={movsError}
      movimentacoes={movsList}
      pagination={movsPagination}
      currentPage={movsCurrentPage}
      onPageChange={handleMovsPageChange}
      adolescente={movsAdolescente}
      showNovaButton={showNovaButton}
    />
  );

  // Gates
  if (initialLoading && adolescentes.length === 0) {
    return (
      <div className={panelThemePageClassName}>
        <LoadingSpinner text="Carregando adolescentes..." />
      </div>
    );
  }

  if (error?.kind === 'permission') {
    return (
      <div className={panelThemePageClassName}>
        <PermissionDeniedAlert
          requiredPermission="adolescentes.view"
          action="visualizar a lista de adolescentes"
        />
      </div>
    );
  }

  if (error && adolescentes.length === 0) {
    return (
      <div className={panelThemePageClassName}>
        <AlertException error={error} fallback="Não foi possível carregar os adolescentes." />
      </div>
    );
  }

  return (
    <div className={panelThemePageClassName}>
      <div className="content-header">
        <h1 className="content-title">Adolescentes</h1>
        <p className="content-subtitle">Lista de adolescentes cadastrados no sistema</p>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="input-group" style={{ maxWidth: '400px' }}>
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (currentPage !== 1) {
                    setCurrentPage(1);
                  }
                }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary small">Registros por página</span>
              <AppSelect
                className="adolescentes-per-page-select"
                ariaLabel="Registros por página"
                options={perPageOptions}
                value={perPage}
                onChange={handlePerPageChange}
              />
            </div>
            <PermissionGuard resource="adolescentes" action="create">
              <Link to="/painel/adolescentes/novo" className="btn btn-primary">
                <i className="bi bi-plus-lg me-2"></i>
                Novo
              </Link>
            </PermissionGuard>
          </div>

          <div className="mb-2">
            <div className="d-flex flex-column align-items-start">
              {isSearchBelowMinLength && (
                <small className="text-secondary">
                  Digite ao menos {MIN_SEARCH_LENGTH} caracteres para filtrar.
                </small>
              )}
              {isListLoading && (
                <small className="text-primary fw-semibold d-inline-flex align-items-center mt-1">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Atualizando lista...
                </small>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-3">
              <AlertException error={error} fallback={'Não foi possível atualizar a lista de adolescentes.'} />
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Data de Nascimento</th>
                  <th>Sexo</th>
                  <th>Nome da Mãe</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {adolescentes.length > 0 ? (
                  adolescentes.map((a) => (
                    <tr key={a.id}>
                      <td data-label="Nome">{formatAdolescenteNome(a)}</td>
                      <td data-label="Data de Nascimento">{fmtDate(a.dt_nasc_adolescente)}</td>
                      <td data-label="Sexo">{a.sexo || '—'}</td>
                      <td data-label="Nome da Mãe">{a.nome_da_mae || '—'}</td>
                      <td className="table-actions text-end" data-label="Ações">
                        <div className="btn-group" role="group" aria-label="Ações">
                          <PermissionGuard resource="movimentacoes" action="create">
                            <button
                              className="btn btn-sm btn-outline-secondary btn-action-mov"
                              title="Ver Movimentações"
                              onClick={() => openMovsModal(a)}
                            >
                              <i className="bi bi-list-ul action-icon"></i> Movimentar
                            </button>
                          </PermissionGuard>

                          <button
                            className="btn btn-sm btn-outline-secondary btn-action-view"
                            title="Visualizar"
                            onClick={() => openViewModal(a.id)}
                          >
                            <i className="bi bi-eye action-icon"></i>
                          </button>

                          <PermissionGuard resource="adolescentes" action="edit">
                            <Link
                              to={`/painel/adolescentes/${a.id}/editar`}
                              className="btn btn-sm btn-outline-warning btn-action-edit"
                              title="Editar Adolescente"
                            >
                              <i className="bi bi-pencil action-icon"></i>
                            </Link>
                          </PermissionGuard>

                          {canShowDeleteAdolescente && (
                            <button
                              className="btn btn-sm btn-outline-danger btn-action-delete"
                              title="Excluir Adolescente"
                              onClick={() => openDeleteModal(a)}
                            >
                              <i className="bi bi-trash action-icon"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      Nenhum adolescente encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span style={panelTheme === 'light' ? { color: '#000000' } : undefined}>
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> •{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isListLoading}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page || isListLoading}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Visualizar Adolescente */}
      {showViewModal && (
        <div className={`modal fade show panel-themed-modal panel-themed-modal--${panelTheme}`} style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-light panel-themed-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Dados do Adolescente</h5>
                <button
                  type="button"
                  className={panelTheme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
                  onClick={closeViewModal}
                ></button>
              </div>
              <div className="modal-body">
                {viewLoading && <LoadingSpinner text="Carregando dados..." />}
                {viewError && <div className="alert alert-danger">{viewError}</div>}
                {!viewLoading && !viewError && selectedAdolescente && (
                  <>
                    <div className="mb-2">
                      <small className="text-secondary d-block">Nome</small>
                      <span className="text-light">{formatAdolescenteNome(selectedAdolescente)}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-secondary d-block">Data de Nascimento</small>
                      <span className="text-light">{fmtDate(selectedAdolescente.dt_nasc_adolescente)}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-secondary d-block">Sexo</small>
                      <span className="text-light">{selectedAdolescente.sexo || '—'}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-secondary d-block">Nome da Mãe</small>
                      <span className="text-light">{selectedAdolescente.nome_da_mae || '—'}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-secondary d-block">ID Jornada</small>
                      <span className="text-light">{selectedAdolescente.jornada_id || '—'}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeViewModal}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Movimentações do Adolescente */}
      {showMovsModal && (
        <div className={`modal fade show panel-themed-modal panel-themed-modal--${panelTheme}`} style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-light panel-themed-modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-center">
                  Movimentações <br /> {formatAdolescenteNome(movsAdolescente)}
                </h5>
                <button
                  type="button"
                  className={panelTheme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
                  onClick={closeMovsModal}
                ></button>
              </div>
              <div className="modal-body movs-modal-lista-movimentacoes">
                {renderMovimentacoesList({ showNovaButton: true })}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeMovsModal}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Excluir Adolescente */}
      {showDeleteModal && (
        <div className={`modal fade show panel-themed-modal panel-themed-modal--${panelTheme}`} style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-light panel-themed-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Exclusão</h5>
                <button
                  type="button"
                  className={panelTheme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Tem certeza que deseja excluir o adolescente{' '}
                  <strong>{formatAdolescenteNome(deleteTarget)}</strong>?
                </p>                

                {deleteCheckingFinalizada && (
                  <LoadingSpinner text="Validando existência de movimentações finalizadas..." />
                )}

                {!deleteCheckingFinalizada && deleteHasFinalizada && (
                  <div className="alert alert-danger">
                    Não é permitido excluir adolescente que possua movimentação finalizada vinculada.
                  </div>
                )}

                {!deleteCheckingFinalizada && !deleteHasFinalizada && movsList.length > 0 && (
                  <div className="alert alert-warning">
                    Atenção: ao excluir este adolescente, todas as movimentações vinculadas também serão excluídas.
                  </div>
                )}

                {deleteError && <div className="alert alert-danger">{deleteError}</div>}

                <div className="movs-modal-lista-movimentacoes mt-3">
                  {renderMovimentacoesList({ showNovaButton: false })}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDeleteModal}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteAdolescente}
                  disabled={
                    deleteCheckingFinalizada
                    || deleteHasFinalizada
                    || deletingAdolescente
                  }
                >
                  {deletingAdolescente ? 'Excluindo...' : 'Excluir Adolescente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdolescentesListPage;
